import { createRoot } from "react-dom/client";
import { AlertTriangle } from "lucide-react";

/**
 * Imperative confirmation popup. Returns a Promise<boolean> that resolves true when
 * the user confirms. Styled to match the app's dark theme.
 *
 *   if (!(await confirmAction({ message: "This will permanently delete your post." }))) return;
 */
export function confirmAction({
    title = "Are you sure?",
    message = "This action can't be undone.",
    confirmText = "Delete",
    cancelText = "Cancel",
    destructive = true,
} = {}) {
    return new Promise((resolve) => {
        const host = document.createElement("div");
        document.body.appendChild(host);
        const root = createRoot(host);
        const done = (val) => { root.unmount(); host.remove(); resolve(val); };
        root.render(
            <ConfirmModal
                title={title}
                message={message}
                confirmText={confirmText}
                cancelText={cancelText}
                destructive={destructive}
                onConfirm={() => done(true)}
                onCancel={() => done(false)}
            />
        );
    });
}

function ConfirmModal({ title, message, confirmText, cancelText, destructive, onConfirm, onCancel }) {
    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            data-testid="confirm-dialog"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
        >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <div className="relative w-full max-w-sm border border-[#2a2a2a] bg-[#141414] p-6 shadow-2xl" role="alertdialog" aria-modal="true">
                <div className="flex items-start gap-3">
                    <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${destructive ? "bg-red-500/10 text-red-500" : "bg-[#ff4500]/10 text-[#ff4500]"}`}>
                        <AlertTriangle className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                        <h3 className="font-display text-lg font-bold text-white leading-tight" data-testid="confirm-dialog-title">{title}</h3>
                        <p className="mt-1 text-sm text-[#a0a0a0]" data-testid="confirm-dialog-message">{message}</p>
                    </div>
                </div>
                <div className="mt-6 flex items-center justify-end gap-2">
                    <button type="button" onClick={onCancel} data-testid="confirm-dialog-cancel"
                        className="px-4 py-2 text-xs uppercase tracking-[0.15em] text-[#a0a0a0] hover:text-white transition-colors">
                        {cancelText}
                    </button>
                    <button type="button" onClick={onConfirm} data-testid="confirm-dialog-confirm" autoFocus
                        className={`px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors ${destructive ? "bg-red-600 hover:bg-red-500" : "bg-[#ff4500] hover:bg-[#ff6a33]"}`}>
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
}
